import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { analyzeContent, parseMixedContent } from '../../utils/mathDetection';
import { MathRendererProps } from '../../types'


/**
 * Renders mixed content by parsing and rendering segments appropriately
 */
const renderMixedContent = (
  text: string,
  display: 'inline' | 'block',
  className: string
): React.ReactElement => {
  const segments = parseMixedContent(text);

  if (segments.length === 0) {
    return <span className={className}>{text}</span>;
  }

  if (display === 'block') {
    return (
      <div className={className} style={{ whiteSpace: 'pre-wrap' }}>
        {segments.map((segment, index) => {
          if (segment.type === 'math') {
            try {
              return (
                <InlineMath key={index} math={segment.content} />
              );
            } catch (error) {
              // If math rendering fails, fall back to plain text
              return <span key={index}>{segment.content}</span>;
            }
          } else {
            return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{segment.content}</span>;
          }
        })}
      </div>
    );
  } else {
    return (
      <span className={className} style={{ whiteSpace: 'pre-wrap' }}>
        {segments.map((segment, index) => {
          if (segment.type === 'math') {
            try {
              return (
                <InlineMath key={index} math={segment.content} />
              );
            } catch (error) {
              // If math rendering fails, fall back to plain text
              return <span key={index}>{segment.content}</span>;
            }
          } else {
            return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{segment.content}</span>;
          }
        })}
      </span>
    );
  }
};

/**
 * MathRenderer Component
 *
 * A smart wrapper around react-katex that provides:
 * - Automatic detection of mathematical content in text
 * - Fallback to plain text rendering for non-mathematical content
 * - Error handling for invalid LaTeX expressions
 * - Support for both inline and block math rendering
 * - Consistent styling and error states
 * - Development-friendly error messages
 *
 * This component automatically determines whether the input text contains
 * mathematical notation and chooses the appropriate rendering method.
 * For plain text, it renders as a normal span/div. For mathematical content,
 * it uses KaTeX for proper mathematical typography.
 *
 * @example
 * ```tsx
 * // Automatically detects and renders math
 * <MathRenderer math="y = 2x + 1" display="inline" />
 *
 * // Plain text renders normally
 * <MathRenderer math="This is just text" display="inline" />
 *
 * // Force math rendering if needed
 * <MathRenderer math="x^2" forceMath={true} display="block" />
 * ```
 */
export const MathRenderer: React.FC<MathRendererProps> = ({
  math,
  display = 'inline',
  className = '',
  errorMessage = 'Error rendering math expression',
  showErrorDetails = process.env.NODE_ENV === 'development',
  forceMath = false
}) => {
  // Validate input
  if (!math || typeof math !== 'string') {
    return (
      <span className={`text-red-500 text-sm ${className}`}>
        {showErrorDetails ? 'Math expression is required and must be a string' : errorMessage}
      </span>
    );
  }

  // Sanitize the math string (basic validation)
  const sanitizedMath = math.trim();
  if (sanitizedMath.length === 0) {
    return (
      <span className={`text-red-500 text-sm ${className}`}>
        {showErrorDetails ? 'Math expression cannot be empty' : errorMessage}
      </span>
    );
  }

  // Analyze the content to determine the best rendering strategy
  const analysis = analyzeContent(sanitizedMath);

  // Force math rendering if requested
  if (forceMath) {
    analysis.recommendedStrategy = 'full-math';
  }

  // Handle based on the analysis
  switch (analysis.recommendedStrategy) {
    case 'plain-text':
      // No math detected, render as plain text
      if (display === 'block') {
        return (
          <div className={className} style={{ whiteSpace: 'pre-wrap' }}>
            {sanitizedMath}
          </div>
        );
      } else {
        return (
          <span className={className} style={{ whiteSpace: 'pre-wrap' }}>
            {sanitizedMath}
          </span>
        );
      }

    case 'full-math':
      // Pure math or forced math rendering
      break; // Continue to KaTeX rendering below

    case 'parse-mixed':
      // Mixed content - parse and render segments
      return renderMixedContent(sanitizedMath, display, className);

    default:
      // Fallback to plain text
      return (
        <span className={className} style={{ whiteSpace: 'pre-wrap' }}>
          {sanitizedMath}
        </span>
      );
  }

  try {
    if (display === 'block') {
      return (
        <div className={`math-block ${className}`}>
          <BlockMath math={sanitizedMath} />
        </div>
      );
    } else {
      return (
        <span className={`math-inline ${className}`}>
          <InlineMath math={sanitizedMath} />
        </span>
      );
    }
  } catch (error) {
    // Handle KaTeX rendering errors gracefully
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';

    return (
      <span className={`inline-flex items-center px-2 py-1 bg-red-50 text-red-700 text-sm rounded border border-red-200 ${className}`} title={showErrorDetails ? `LaTeX Error: ${errorMsg}` : errorMessage}>
        <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <span className="font-mono text-xs">
          {showErrorDetails ? `LaTeX Error: ${sanitizedMath}` : errorMessage}
        </span>
      </span>
    );
  }
};
